/**
 * Fonctions utilitaires pour la page des commandes (Prestataire)
 */

import { Commande, StatusActionMapping } from './types';
import { 
  STATUS_ACTION_MAP_MS, 
  STATUS_ACTION_MAP_BC, 
  STATUS_ACTION_MAP_BCI 
} from './statusConfig';

// ============================================================================
// Conversion de chaîne en classe CSS
// ============================================================================

/**
 * Convertit une chaîne en classe CSS valide
 * Ex: "En attente de l'offre" → "en-attente-de-loffre"
 */
export function toCssClass(str: string): string {
  if (!str) return '';
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9-]/g, '')
    .toLowerCase();
}

// ============================================================================
// Récupération du rôle utilisateur
// ============================================================================

/**
 * Récupère le rôle utilisateur depuis le localStorage
 */
export function getUserRoleFromStorage(): string {
  try {
    const userJson = localStorage.getItem('user');
    if (userJson) {
      const userData = JSON.parse(userJson);
      return userData.role || '';
    }
  } catch (error) {
    console.error('Erreur lors de la récupération du rôle utilisateur:', error);
  }
  return '';
}

// ============================================================================
// Mapping des actions par statut
// ============================================================================

/**
 * Détermine l'action à afficher en fonction du statut et du rôle utilisateur
 */
export function getStatusActionMapping(
  statusId: number, 
  userRole: string, 
  commande: Commande
): StatusActionMapping {
  // Sélectionner le bon mapping selon la source
  let mappingSet;
  switch (commande.source) {
    case 'BC':
      mappingSet = STATUS_ACTION_MAP_BC;
      break;
    case 'BCI':
      mappingSet = STATUS_ACTION_MAP_BCI;
      break;
    default:
      mappingSet = STATUS_ACTION_MAP_MS;
  }

  const mapping = mappingSet[statusId];

  // Si pas de mapping pour ce statut
  if (!mapping) {
    return {
      buttonText: '',
      action: '',
      showButton: false,
      disableButton: true
    };
  }

  // Vérifie si le rôle est autorisé
  const showButton =
    mapping.buttonText !== '' &&
    (mapping.forRoles.some(role => 
      userRole.toLowerCase().includes(role.toLowerCase())
    ) || mapping.forRoles.includes('*'));

  // Active le bouton seulement si le mapping l'autorise
  const disableButton = mapping.buttonText === '';

  return {
    buttonText: mapping.buttonText,
    action: mapping.action,
    showButton,
    disableButton
  };
}

// ============================================================================
// Transformation des données API
// ============================================================================

/**
 * Transforme les données brutes de l'API en format exploitable
 */
export function transformCommandeData(commande: Commande): Commande {
  return {
    ...commande,
    nom: commande.operation?.nom || commande.nom || `Commande #${commande.id}`,
    delais: commande.delais || 0,
    visite: commande.visite === null ? false : commande.visite,
    note_financiere: commande.note_financiere || 0,
    note_total: commande.note_total || 0,
    lot_technique: commande.lot_technique || { id: 0, nom: 'Non défini' },
    cree_par: commande.cree_par || 'Système',
    organisme_nom: commande.operation?.organisme_nom || 'Non défini'
  };
}

/**
 * Transforme un tableau de commandes
 */
export function transformCommandesData(commandes: Commande[]): Commande[] {
  return commandes.map(transformCommandeData);
}

// ============================================================================
// Tri des paniers importés
// ============================================================================

/**
 * Trie les paniers importés par priorité (status.id === 11 en premier)
 */
export function sortPaniersImportesByPriority(commandes: Commande[]): Commande[] {
  return [...commandes].sort((a, b) => {
    const aIsAValider = a.status?.id === 11;
    const bIsAValider = b.status?.id === 11;

    if (aIsAValider !== bIsAValider) {
      return aIsAValider ? -1 : 1;
    }

    const dateA = new Date(
      a.operation?.date_debut_operation || a.created_at || 0
    ).getTime();
    const dateB = new Date(
      b.operation?.date_debut_operation || b.created_at || 0
    ).getTime();
    
    return dateB - dateA;
  });
}

// ============================================================================
// Formatage des dates
// ============================================================================

/**
 * Formate une date en format français (JJ/MM/AAAA)
 */
export function formatDateFR(dateString: string | null | undefined): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR');
}

// ============================================================================
// Détermination de la redirection au clic sur une ligne
// ============================================================================

interface RedirectionInfo {
  basePath: string;
  tab: number;
  action: string;
}

/**
 * Détermine la redirection au clic sur une commande
 */
export function getCommandeRedirectionInfo(commande: Commande): RedirectionInfo {
  let action = '';
  let tab = 0;

  const statusNom = commande.status?.nom;
  const statusId = commande.status?.id;
  const source = commande.source?.toLowerCase().trim();

  // Logique de redirection basée sur le statut
  if (statusNom === 'Brouillon') {
    action = 'reprendre';
    tab = 0;
  } else if (['En attente de l\'analyse', 'En analyse'].includes(statusNom)) {
    action = '';
    tab = 1;
  } else if (source === 'bc' && statusId === 11) {
    // BC avec statut 11 (À chiffrer) → tab 2 (Offre)
    action = 'chiffrer';
    tab = 2;
  } else if (source === 'bc' && (statusId === 13 || statusNom === "En attente de décision de l'acheteur")) {
    // BC en attente de décision → tab 2 (Offre) en lecture seule
    action = 'view';
    tab = 2;
  } else if (source === 'bci' && statusId === 11) {
    action = 'valider';
    tab = 0;
  } else if (statusId >= 8 && statusId < 12) {
    action = 'signer';
    tab = 2;
  } else if (statusNom === 'À candidater') {
    action = 'candidater';
    tab = 1;
  } else if (statusNom === 'À accepter') {
    action = 'accepter';
    tab = 2;
  } else {
    action = 'view';
    tab = 0;
  }

  // Détermination du chemin selon la source
  let basePath: string;
  switch (source) {
    case 'bc':
      basePath = '/commande-bc';
      break;
    case 'bci':
      basePath = '/commande-bci';
      break;
    default:
      basePath = '/commande-ms';
  }

  return { basePath, tab, action };
}

// ============================================================================
// Renommage des statuts pour l'affichage
// ============================================================================

/**
 * Retourne le nom d'affichage du statut selon la source
 */
export function getDisplayStatusName(
  statusId: number, 
  statusNom: string, 
  source: string
): string {
  if (source === 'MS') {
    if (statusId === 6) return 'Refusé';
    if (statusId === 11) return 'Accepté';
    if (statusId === 12) return 'Expiré';
  }

  if (source === 'BC') {
    if (statusId === 8) return 'Refusé';
    if (statusId === 7) return 'Accepté';
  }

  return statusNom;
}
